import { useEffect, useRef } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  Code, ShoppingCart, Building2, ArrowRight, CheckCircle2,
  Globe, Shield, Headphones, Zap, BarChart3, Monitor
} from "lucide-react";
import { useTranslation } from "@/i18n/translations";

function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }} transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>{children}</motion.div>
  );
}

export default function PartnersPage() {
  const { t: tr } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const PARTNER_TYPES = [
    {
      icon: ShoppingCart,
      title: tr("partners.t1"),
      desc: tr("partners.t1D"),
      features: ["API RESTful", "SDK Android", "PCI-DSS", "Sandbox"]
    },
    {
      icon: Code,
      title: tr("partners.t2"),
      desc: tr("partners.t2D"),
      features: ["Documentation", "Support technique", "Certification", "Commission"]
    },
    {
      icon: Building2,
      title: tr("partners.t3"),
      desc: tr("partners.t3D"),
      features: ["Marges", "Marketing", "Formation", "Account manager"]
    },
    {
      icon: Globe,
      title: tr("partners.t4"),
      desc: tr("partners.t4D"),
      features: ["Multi-sites", "Dashboard", "Volume", "Support"]
    },
  ];

  const INTEGRATION_FEATURES = [
    { icon: Code, title: "API RESTful", desc: tr("partners.integDesc") },
    { icon: Shield, title: "PCI-DSS Niveau 1", desc: tr("partners.integDesc") },
    { icon: Monitor, title: "SDK Android", desc: tr("partners.integDesc") },
    { icon: Zap, title: "Sandbox", desc: tr("partners.integDesc") },
    { icon: BarChart3, title: "Dashboard", desc: tr("partners.integDesc") },
    { icon: Headphones, title: tr("partners.support"), desc: tr("partners.integDesc") },
  ];

  const STEPS = [
    { num: "01", title: tr("partners.p1"), desc: tr("partners.p1D") },
    { num: "02", title: tr("partners.p2"), desc: tr("partners.p2D") },
    { num: "03", title: tr("partners.p3"), desc: tr("partners.p3D") },
    { num: "04", title: tr("partners.p4"), desc: tr("partners.p4D") },
  ];

  const WHY_FEATURES = [
    tr("partners.whyF1"), tr("partners.whyF2"), tr("partners.whyF3"),
    tr("partners.whyF4"), tr("partners.whyF5"), tr("partners.whyF6"),
  ];

  return (
    <div data-testid="partners-page">
      <SEO page="partners" />
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#1B2B5B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("partners.tag")}</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mt-4 leading-[1.1]">
                {tr("partners.title")}
              </h1>
              <p className="text-base md:text-lg text-slate-300 mt-6 leading-relaxed">
                {tr("partners.desc")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 font-semibold bg-[#00C853] text-white rounded-full px-7 py-3 text-sm hover:bg-[#00A844] transition-all">
                  {tr("partners.become")} <ArrowRight size={16} />
                </Link>
                <a href="#integration" className="inline-flex items-center justify-center font-semibold border-2 border-white/20 text-white rounded-full px-7 py-3 text-sm hover:bg-white/10 transition-all">
                  {tr("partners.seeApi")}
                </a>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: "50+", label: tr("partners.active") },
                  { value: "API", label: tr("partners.apiLabel") },
                  { value: "24/7", label: tr("partners.support") },
                  { value: "48h", label: tr("partners.onboard") },
                ].map((s, i) => (
                  <div key={i} className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-5 text-center">
                    <div className="text-2xl font-bold text-[#00C853]">{s.value}</div>
                    <div className="text-xs text-slate-400 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("partners.typesTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">
              {tr("partners.typesTitle")}
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PARTNER_TYPES.map((type, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="feature-card bg-[#F8F9FA] rounded-xl border border-slate-100 p-8 h-full">
                  <div className="w-11 h-11 rounded-lg bg-[#1B2B5B]/5 flex items-center justify-center mb-5">
                    <type.icon size={22} className="text-[#1B2B5B]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0A1128] mb-2">{type.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed mb-5">{type.desc}</p>
                  <ul className="space-y-2">
                    {type.features.map((f, fi) => (
                      <li key={fi} className="flex items-center gap-2">
                        <CheckCircle2 size={14} className="text-[#00C853] shrink-0" />
                        <span className="text-sm text-[#475569]">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section id="integration" className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("partners.integTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">
              {tr("partners.integTitle")}
            </h2>
            <p className="text-base text-[#475569] mt-4 max-w-lg mx-auto">
              {tr("partners.integDesc")}
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INTEGRATION_FEATURES.map((feat, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="feature-card bg-white rounded-xl border border-slate-200 p-8 h-full">
                  <div className="w-11 h-11 rounded-lg bg-[#00C853]/10 flex items-center justify-center mb-5">
                    <feat.icon size={22} className="text-[#00C853]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0A1128] mb-2">{feat.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed">{feat.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <AnimatedSection>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("partners.whyTag")}</span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">
                {tr("partners.whyTitle")}
              </h2>
              <p className="text-base text-[#475569] mt-4 leading-relaxed">
                {tr("partners.whyDesc")}
              </p>
              <ul className="mt-6 space-y-4">
                {WHY_FEATURES.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#00C853] mt-0.5 shrink-0" />
                    <span className="text-sm text-[#475569]">{item}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="bg-[#1B2B5B] rounded-2xl p-8 lg:p-10">
                <h3 className="text-xl font-bold text-white mb-6">{tr("partners.partnersSay")}</h3>
                <div className="space-y-6">
                  {[
                    { text: "L'integration avec notre POS a ete rapide et fluide.", name: "Editeur POS, Zurich" },
                    { text: "Le fait que les terminaux soient gratuits facilite la vente.", name: "Revendeur, Geneve" },
                    { text: "Le programme de commission est transparent.", name: "Integrateur, Lausanne" },
                  ].map((q, i) => (
                    <div key={i} className="border-l-2 border-[#00C853] pl-4">
                      <p className="text-sm text-slate-300 italic">"{q.text}"</p>
                      <p className="text-xs text-slate-500 mt-2">{q.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("partners.procTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">
              {tr("partners.procTitle")}
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map((step, i) => (
              <AnimatedSection key={i} delay={i * 0.12}>
                <div className="relative">
                  <span className="text-5xl font-bold text-[#1B2B5B]/5">{step.num}</span>
                  <h3 className="text-lg font-semibold text-[#0A1128] mt-2">{step.title}</h3>
                  <p className="text-sm text-[#475569] mt-2 leading-relaxed">{step.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#1B2B5B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              {tr("partners.ctaTitle")}
            </h2>
            <p className="text-base text-slate-300 mt-4 max-w-md mx-auto">
              {tr("partners.ctaDesc")}
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 mt-8">
              <Link
                to="/contact"
                data-testid="partners-cta"
                className="inline-flex items-center justify-center gap-2 font-semibold bg-[#00C853] text-white rounded-full px-8 py-3.5 text-sm hover:bg-[#00A844] transition-all"
              >
                {tr("partners.become")} <ArrowRight size={16} />
              </Link>
              <a
                href="mailto:contact@ayapay.ch"
                className="inline-flex items-center justify-center font-semibold border-2 border-white/20 text-white rounded-full px-8 py-3.5 text-sm hover:bg-white/10 transition-all"
              >
                contact@ayapay.ch
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
